// src/components/digital_twin/twin_engine/twinData.js
// Every backend source the asset twin reads, with tolerant normalisers. A source that fails or has
// nothing returns { status: 'error' | 'empty' } — it is shown as missing, never filled with defaults.
import api from '../../../services/api';
import * as ActiveAlertService from '../../../services/activeSensorAlertService';

export const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const unwrap = (res) => { const b = res?.data; return b && typeof b === 'object' && 'data' in b && b.success !== undefined ? b.data : (b?.data ?? b); };
const arr = (v) => (Array.isArray(v) ? v : Array.isArray(v?.data) ? v.data : Array.isArray(v?.rows) ? v.rows : Array.isArray(v?.readings) ? v.readings : []);
const prob = (v) => { const n = num(v); if (n === null) return null; return n > 1 ? Math.min(1, n / 100) : Math.max(0, n); };

export function normHealth(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const h = raw.health || raw;
  const out = {
    score: num(h.health_score ?? h.score),
    status: (h.health_status ?? h.status ?? null) && String(h.health_status ?? h.status).toLowerCase(),
    trend: h.trend ?? h.health_trend ?? null,
    failureProbability: prob(h.failure_probability ?? h.failure_prob ?? h.risk_probability),
    rulDays: num(h.predicted_rul_days ?? h.rul_days ?? h.rul ?? h.remaining_useful_life_days),
    ttfHours: num(h.estimated_ttf_hours),
    degradationRate: num(h.degradation_rate),
    confidence: prob(h.confidence ?? h.model_confidence),
    modelVersion: h.model_version ?? h.model_version_used ?? null,
    updatedAt: h.recorded_at ?? h.updated_at ?? h.predicted_at ?? null,
    forecast: arr(h.forecast_points).map((p) => ({ hours: num(p.hours ?? p.t ?? p.day), value: num(p.predictedHealth ?? p.health ?? p.value) })).filter((p) => p.hours !== null && p.value !== null),
    drivers: arr(h.top_factors ?? h.drivers ?? h.contributing_factors).slice(0, 6),
  };
  return Object.values(out).some((v) => v !== null && !(Array.isArray(v) && !v.length)) ? out : null;
}

export const normHistory = (raw) => arr(raw)
  .map((h) => ({ ts: Date.parse(h.recorded_at ?? h.timestamp ?? h.created_at), value: num(h.health_score ?? h.score) }))
  .filter((p) => Number.isFinite(p.ts) && p.value !== null).sort((a, b) => a.ts - b.ts);

export function normKpis(raw) {
  const k = raw?.kpis || raw;
  if (!k || typeof k !== 'object') return null;
  const out = {
    availability: num(k.availability ?? k.availability_pct), oee: num(k.oee ?? k.oee_pct),
    mttr: num(k.mttr_hours ?? k.mttr), mtbf: num(k.mtbf_hours ?? k.mtbf),
    failures: num(k.failureCount ?? k.failure_count), downtime: num(k.totalDowntimeHours ?? k.total_downtime_hours),
  };
  if (out.oee !== null && out.oee <= 1) out.oee = Math.round(out.oee * 1000) / 10;
  return Object.values(out).some((v) => v !== null) ? out : null;
}

export const normMaintenance = (raw) => arr(raw).map((m, i) => ({
  id: m.id ?? i, type: m.maintenance_type ?? m.type ?? m.activity ?? 'Maintenance',
  description: m.description ?? m.notes ?? m.summary ?? '', status: m.status ?? null,
  date: m.maintenance_date ?? m.performed_at ?? m.completed_at ?? m.scheduled_date ?? m.created_at ?? null,
  cost: num(m.cost ?? m.total_cost), technician: m.technician_name ?? m.performed_by ?? m.technician ?? null,
})).sort((a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0));

export const normAlert = (a) => ({
  id: a.id, severity: String(a.severity || 'warning').toLowerCase(), message: a.alert_message ?? a.message ?? a.condition_triggered ?? 'Alert',
  status: String(a.status || 'active').toLowerCase(), createdAt: a.triggered_at ?? a.created_at ?? null, sensorId: a.sensor_id ?? a.device_id ?? null,
  workOrderId: a.work_order_id ?? null,
});

/** One source → { status, data, at, ms, error }. */
async function source(fn, isEmpty = (d) => d == null || (Array.isArray(d) && !d.length)) {
  const t0 = performance.now();
  try {
    const data = await fn();
    return { status: isEmpty(data) ? 'empty' : 'ok', data, at: Date.now(), ms: Math.round(performance.now() - t0), error: null };
  } catch (e) {
    const code = e?.response?.status;
    return { status: code === 404 ? 'empty' : 'error', data: null, at: Date.now(), ms: Math.round(performance.now() - t0), error: e?.response?.data?.message || e?.response?.data?.error || e.message };
  }
}

export const SOURCE_LABELS = Object.freeze({
  health: 'ML health model', history: 'Health history', kpis: 'Reliability KPIs', maintenance: 'Maintenance history',
  alerts: 'Active alerts', elevation: 'Ground elevation (TessaDEM)', wellIntel: 'Well intelligence', sensorHistory: 'Sensor history',
  historical: 'Historical snapshot',
});

/** Fetch everything for one asset in parallel. */
export async function fetchAssetTwin({ asset, profile, sensors = [], signal }) {
  const id = encodeURIComponent(asset.id);
  const opt = signal ? { signal } : undefined;
  const sensorIds = new Set(sensors.map((s) => String(s.device_id ?? s.id)));
  const [health, history, kpis, maintenance, alerts, elevation, wellIntel, sensorHistory] = await Promise.all([
    source(async () => normHealth(unwrap(await api.get(`/predictive-maintenance/assets/${id}/health`, opt)))),
    source(async () => normHistory(unwrap(await api.get(`/predictive-maintenance/assets/${id}/health-history`, opt)))),
    source(async () => normKpis(unwrap(await api.get(`/analytics/asset-performance?asset_id=${id}`, opt)))),
    source(async () => normMaintenance(unwrap(await api.get(`/asset-maintenance/asset/${id}`, opt)))),
    source(async () => {
      const r = await ActiveAlertService.fetchActiveAlerts();
      const list = Array.isArray(r) ? r : Array.isArray(r?.data) ? r.data : Array.isArray(r?.data?.data) ? r.data.data : [];
      return list.filter((a) => String(a.asset_id) === String(asset.id) || sensorIds.has(String(a.sensor_id ?? a.device_id))).map(normAlert)
        .filter((a) => !['resolved', 'closed'].includes(a.status));
    }),
    source(async () => unwrap(await api.get(`/elevation/assets/${id}`, opt)), (d) => !d || (d.ground_elevation_m == null && !d.profile)),
    profile.wellIntel ? source(async () => unwrap(await api.get(`/wells/${id}/intel`, opt))) : Promise.resolve({ status: 'skipped', data: null }),
    sensors.length ? source(async () => {
      const out = {};
      await Promise.all(sensors.slice(0, 6).map(async (s) => {
        const dev = encodeURIComponent(s.device_id ?? s.id);
        const url = String(s.type).toLowerCase() === 'water_level' ? `/water-level/${dev}?limit=96` : `/sensors/${dev}/readings?limit=96`;
        try { out[String(s.device_id ?? s.id)] = arr(unwrap(await api.get(url, opt))); } catch (_) { /* one sensor missing is fine */ }
      }));
      return out;
    }, (d) => !d || !Object.values(d).some((v) => v.length)) : Promise.resolve({ status: 'skipped', data: null }),
  ]);
  return { health, history, kpis, maintenance, alerts, elevation, wellIntel, sensorHistory };
}

export async function fetchHistoricalTwin(assetId, ts, signal) {
  return source(async () => unwrap(await api.get(`/ai/historical-twin-state?assetId=${encodeURIComponent(assetId)}&timestamp=${new Date(ts).toISOString()}`, signal ? { signal } : undefined)));
}

export const ackAlert = (id) => ActiveAlertService.updateActiveAlert(id, { status: 'acknowledged', notes: 'Acknowledged in the Digital Twin' });
export const resolveAlert = (id, note) => ActiveAlertService.updateActiveAlert(id, { status: 'resolved', resolution_notes: note || 'Resolved in the Digital Twin' });
